import { useEffect } from 'react'
import { type NextPage } from 'next'
import { LUCY_API_BASE_URL } from 'shared/envs'
import { useExperiment } from 'shared/experiment'
import { stringFormat } from 'shared/format-utils'
import {
  type LucyChatConfig,
  type LucyFeatureFlags,
  createLucyDatadogLogger,
  initializeLucyChat,
  LucyChatWidget,
  setLucyChatFlags
} from 'shared/lucy-chat'
import { isomorphicCookies } from 'shared/storage'

import { useIsOneStone } from '../../deus-ex-machina/hooks/use-is-one-stone'
import { type WithDeusExMachinaProps } from '../../deus-ex-machina/ports'
import { authenticator } from '../../../core/authenticator/authenticator'

const LUCY_CHAT_EXPERIMENT = 'engineering-cj-core-lucy-web-chat'
const LUCY_CHAT_INCIDENT_EXPERIMENT = 'engineering-cj-core-lucy-web-chat-incident'
const LUCY_CHAT_INCIDENT_WHATSAPP_EXPERIMENT = 'engineering-cj-core-lucy-web-chat-incident-whatsapp'

function resolveIncidentMode(isIncidentEnabled: boolean, isWhatsappEnabled: boolean): LucyFeatureFlags['incidentMode'] {
  if (isWhatsappEnabled) return 'chat'
  if (isIncidentEnabled) return 'general'
  return undefined
}

function buildLucyConfig({ subject, entity }: WithDeusExMachinaProps, flags: LucyFeatureFlags): LucyChatConfig | null {
  const baseUrl = LUCY_API_BASE_URL()
  if (!baseUrl) return null

  return {
    api: { baseUrl },
    auth: {
      getToken: async () => {
        try {
          const { token } = await authenticator.getValidTokens()
          return token
        } catch {
          // Refresh falhou (erro de rede ou similar) — retorna o token atual do cookie.
          // O servidor vai rejeitar com 401, o SDK trata como erro normal de sessão
          // sem acionar a tela de incidente.
          return isomorphicCookies.getAll().token
        }
      }
    },
    user: {
      name: subject?.displayName,
      document: subject?.document,
      phone: subject?.phoneNumber ? stringFormat.onlyNumbers(subject.phoneNumber) : undefined,
      accountId: entity?.paymentAccount?.id
    },
    flags,
    logger: createLucyDatadogLogger(subject?.id)
  }
}

export function withLucyChat(Page: NextPage<WithDeusExMachinaProps>) {
  function WrappedPage(props: WithDeusExMachinaProps) {
    const { isFeatureFlagEnabled } = useExperiment(props?.subject?.id, LUCY_CHAT_EXPERIMENT)
    const { isFeatureFlagEnabled: isIncidentEnabled } = useExperiment(props?.subject?.id, LUCY_CHAT_INCIDENT_EXPERIMENT)
    const { isFeatureFlagEnabled: isIncidentWhatsappEnabled } = useExperiment(
      props?.subject?.id,
      LUCY_CHAT_INCIDENT_WHATSAPP_EXPERIMENT
    )
    const isOneStone = useIsOneStone()

    const incidentMode = resolveIncidentMode(isIncidentEnabled, isIncidentWhatsappEnabled)
    const isLucyActive = isFeatureFlagEnabled && isOneStone
    const config = isLucyActive ? buildLucyConfig(props, { incidentMode }) : null
    if (config) initializeLucyChat(config)

    // `initializeLucyChat` só aplica `flags` no primeiro setup; propaga mudanças em runtime sem remontar o widget.
    useEffect(() => {
      if (isLucyActive) setLucyChatFlags({ incidentMode })
    }, [isLucyActive, incidentMode])

    return (
      <>
        <Page {...props} />
        {config ? <LucyChatWidget /> : null}
      </>
    )
  }

  WrappedPage.displayName = `withLucyChat(${Page.displayName})`

  return WrappedPage
}
